/* eslint react/no-direct-mutation-state: 0 */

import React from 'react'
import Screen from '../Screen'
import {
  SlidesScreenProps,
  SlidesScreenState,
  SlidesMessage
} from './types'
import styles from './SlidesScreen.module.scss'
import ScreenRowLayout from '../Screen/ScreenRowLayout'
import SlidesControls from '../SlidesControls'
import get from 'lodash/get'
import { addEntry } from '../../lib/audit'

declare global {
  interface Window { SetFrameHeight: any }
}

/** Returns a Presenter Screen with the content type that was passed to it */
class SlidesScreen extends React.Component<SlidesScreenProps, Partial<SlidesScreenState>> {

  state = {
    initialised: false,
    presentation: {
      IsSlideMediaLoaded: index => false,
      GotoSlide: index => {},
      JumpToAnim: (stepIndex, slideIndex, direct) => {},
      GetContentDetails: () => ({ TotalSlides: 0 }),
      GetCurrentAnimationCount: () => 0,
      CurrentStatus: () => ({ slide: 0, step: 0 }),
      ClickCount: () => 0,
      videoStep: () => 0,
      resetSlide: index => {}
    },
    currentSlide: null,
    currentStep: 0,
    fullSlidesUrl: '',
    isAnimating: false
  }

  animationTimeout: number | null = null
  frameRef = React.createRef<HTMLIFrameElement>()
  prevRef: HTMLIFrameElement | null = null

  ANIMATION_TIMEOUT = 120000

  constructor (props) {

    super(props)

    this.handleSlideEvents = this.handleSlideEvents.bind(this)
    this.handleKeyPress = this.handleKeyPress.bind(this)
  }

  componentDidMount () {

    this.prevRef = this.frameRef.current

    this.setState({
      fullSlidesUrl : `${this.props.presentationURL}?slide=${this.props.selectedSlide}&step=${this.props.selectedSlideStep}`
    })

    window.addEventListener('message', this.handleSlideEvents, false)
    document.addEventListener('keydown', this.handleKeyPress)
  }

  componentWillUnmount () {

    if( this.animationTimeout ) clearTimeout( this.animationTimeout )

    window.removeEventListener('message', this.handleSlideEvents, false)
    document.removeEventListener('keydown', this.handleKeyPress)
  }

  componentDidUpdate (prevProps: SlidesScreenProps, prevState: SlidesScreenState) {

    if (this.prevRef !== this.frameRef.current) {

      this.prevRef = this.frameRef.current
      const presentation = get(this.frameRef, 'current.contentWindow.Presentation')

      this.setState({
        presentation
      })
    }

    else if (this.state.initialised &&
      !this.state.isAnimating && (
      this.state.currentSlide !== this.props.selectedSlide ||
      this.props.selectedSlide !== prevProps.selectedSlide ||
      this.props.selectedSlideStep !== prevProps.selectedSlideStep ||
      this.state.initialised !== prevState.initialised ||
      this.state.presentation !== prevState.presentation
    )) {

      const isBackwards = this.props.selectedSlide < prevProps.selectedSlide || (
        this.props.selectedSlide === prevProps.selectedSlide && this.props.selectedSlideStep < prevProps.selectedSlideStep
      )
      if( isBackwards ){

        this.setState({ currentSlide: this.props.selectedSlide, currentStep: 0 })
        this.state.presentation.resetSlide(  this.props.selectedSlide )
        if( prevProps.selectedSlide !== this.props.selectedSlide) {
          this.state.presentation.GotoSlide( this.props.selectedSlide )
        }
      }
      else {
        this.jumpToAnim(this.props.selectedSlide, this.props.selectedSlideStep, true )
      }
    }
  }

  jumpToAnim = (slideIndex, stepIndex, doTransition = false ) => {

    if( !this.state.isAnimating ){
      this.setState({ currentSlide: slideIndex, currentStep: stepIndex })
      this.state.presentation.JumpToAnim(stepIndex, slideIndex, doTransition )
    }
  }

  isInputElement = (target): boolean => [ 'TEXTAREA', 'INPUT' ].includes(target.tagName)

  handleKeyPress = ({ key, target }) => {

    if (!this.isInputElement(target)) {

      if (key === 'ArrowLeft') {

        this.prevHandler()
      }

      if (key === 'ArrowRight' || key === ' ') {

        this.nextHandler()
      }
    }
  }

  handleSlideEvents (event) {

    const { message, payload = {} } = event.data

    switch (message) {

      case SlidesMessage.loadingEnded:
        if (!this.state.initialised) {

          const presentation = get(this.frameRef, 'current.contentWindow.Presentation')
          this.setState({
            initialised: true,
            presentation
          })

        }
        break

      case SlidesMessage.playingEnd:

        if( this.animationTimeout ) {
          window.clearTimeout( this.animationTimeout )
          this.animationTimeout = null
        }
        this.setState(state => ({
          isAnimating : false
        }))
        if (this.state.presentation.CurrentStatus().step !== this.props.selectedSlideStep ||
        this.state.presentation.CurrentStatus().slide !== this.props.selectedSlide) {

          this.state.presentation.resetSlide( this.state.presentation.CurrentStatus().slide )
          this.state.presentation.resetSlide( this.props.selectedSlide )
          this.jumpToAnim(this.props.selectedSlide, this.props.selectedSlideStep, false)
        }
        break

      case SlidesMessage.initialised:
        break

      case SlidesMessage.setFrameHeight:
        this.setFrameHeight(payload)
        break

      case SlidesMessage.videoFailed:
        this.props.onVideoError()
        break


      case SlidesMessage.error:

        this.state.presentation.resetSlide( this.props.selectedSlide )
        this.state.presentation.resetSlide( this.state.presentation.CurrentStatus().slide )
        this.state.presentation.GotoSlide( this.props.selectedSlide )
        break

      case SlidesMessage.playingStart:

        if( this.animationTimeout ) clearTimeout( this.animationTimeout )
        // @ts-ignore
        this.animationTimeout = window.setTimeout( () => {

          this.state.presentation.resetSlide( this.props.selectedSlide )
          this.setState(state => ({
            isAnimating : false
          }))
        }, this.ANIMATION_TIMEOUT )
        this.setState(state => ({
          isAnimating : true
        }))
        break;
      case SlidesMessage.slideChange:
      case SlidesMessage.slideEnd:
      case SlidesMessage.loading:
      case SlidesMessage.progressChange:
      case SlidesMessage.slideOutlineLoaded:
        break
      default:
        if (message) addEntry('PresenterService', 'SlidesScreen', `Unrecognised message from slidesdeck : ${message}`)

    }
  }

  setFrameHeight (height) {

    if (this.frameRef && this.frameRef.current) {

      this.frameRef.current.style.marginTop = `${(this.frameRef.current.clientHeight - height) / 2}px`
    }
  }

  isBackButtonDisabled = () =>  ( this.state.currentSlide === 1 && this.state.currentStep === 0 ) || this.state.isAnimating

  isNextButtonDisabled = () => {

    if( this.state.isAnimating ) return true
    if (this.state.initialised) {

      const { TotalSlides } = this.state.presentation.GetContentDetails()
      const currentAnimationCount = this.state.presentation.ClickCount()
      if (this.state.currentSlide === TotalSlides && this.state.currentStep === currentAnimationCount) {

        return true
      }
    }
    return false
  }

  prevHandler = () => {

    if( !this.state.isAnimating ){
      const presentation = this.state.presentation
      const videoStep = presentation?.videoStep ?? 0
      if (this.props.navigate) {

        const { currentStep, currentSlide } = this.state
        if (currentStep && !videoStep) {

          this.props.navigate(currentSlide, 0)

        }
        else if (currentSlide > 1) {

          this.props.navigate(currentSlide - 1, 0)

        }
      }
    }
  }

  nextHandler = () => {

   if( !this.state.isAnimating ){
      const presentation = this.state.presentation
      const totalAnims = presentation.ClickCount()
      const totalSlides = presentation.GetContentDetails().TotalSlides

      const { currentStep, currentSlide } = this.state

      if (this.props.navigate) {

        if (currentSlide === presentation.CurrentStatus().slide && currentStep < totalAnims) {

          this.props.navigate(currentSlide, currentStep + 1)

        }
        else if (currentSlide < totalSlides) {

          this.props.navigate(currentSlide + 1, 0)

        }
      }
    }
  }

  render () {

    let backDisabled; let nextDisabled
    if (!this.props.isBridge) {

      backDisabled = this.isBackButtonDisabled()
      nextDisabled = this.isNextButtonDisabled()

    }

    return <Screen>
      <ScreenRowLayout>
        <div
          className={styles['slides-iframe-container']}
        >
          <iframe
            id='frame-container'
            title='slide show'
            data-bdd="slides-iframe"
            className={styles['slides-container']}
            src={this.state.fullSlidesUrl}
            ref={this.frameRef}
          />
          <div id='link-blocker'
            className={styles['link-blocker']}
          />
        </div>
        {!this.props.isBridge && <SlidesControls
          backDisabled={backDisabled}
          nextDisabled={nextDisabled}
          totalSlides={this.props.slideDeck && this.props.slideDeck.numberOfSlides ? this.props.slideDeck.numberOfSlides : 0}
          currentSlide={this.props.selectedSlide}
          onJumpToSlidesClick={this.props.onJumpToSlidesClick}
          onEndClick={this.props.onEndClick}
          isPresenterLayoutsActive={this.props.presenterLayoutsActive}
          onPreviousClick={() => !backDisabled && this.prevHandler()}
          onNextClick={() => !nextDisabled && this.nextHandler()}
        />}
      </ScreenRowLayout>
    </Screen>
  }
}

export default SlidesScreen
